/**
 * Named constants for feature flags this app gates on, for the call sites that
 * want one instead of a bare string.
 *
 * This is NOT the list of flags that exist. The `development` environment is
 * that list — create a flag there and the next deploy promotes it to staging
 * and production. Nothing needs adding here for a flag to propagate, and plenty
 * of flags the app reads are not listed below.
 *
 * `sync-feature-flags.ts --verify` checks that every flag name this app reads,
 * whether from here or as a bare string literal, actually exists in
 * development. See packages/backend/scripts/ad-hoc/feature-flags/README.md.
 */
export const FEATURE_FLAGS = {
  MSR3_ADD_PAYMENT_RULE: 'msr3.customerApp.addPaymentRule',
  MSR3_ANNOUNCEMENT_BANNER: 'msr3.customerApp.announcementBanner',
  MSR3_CLIENT_PAGE_ROUTE: 'msr3.customerApp.clientPage.route',
  MSR3_CLIENT_SUMMARY_ROUTE: 'msr3.customerApp.clientSummaryPage.route',
  MSR3_DELETE_BANK_ACCOUNT: 'msr3.customerApp.deleteBankAccount',
  /** Gates the CSV download button on MSR3 list pages (APP-2510). Default off. */
  MSR3_DOWNLOAD_CSV: 'msr3.customerApp.downloadCsv',
  MSR3_ENABLED: 'msr3.customerApp.enabled',
  MSR3_HOME_ROUTE: 'msr3.customerApp.homePage.route',
  /** Gates the Notifications page route and every nav entry that links to it. Default off. */
  MSR3_NOTIFICATIONS_ROUTE: 'msr3.customerApp.notificationsPage.route',
  /** Gates the MSR3 onboarding shell + all five onboarding steps. Default off until the flow is verified. */
  MSR3_ONBOARDING_ROUTE: 'msr3.customerApp.onboardingPage.route',
  /** Gates the Historic Royalties "Paid Royalties" tab + route; needs MSR2's embed/royalties route (APP-2523). Default off. */
  MSR3_PAID_ROYALTIES_TAB:
    'msr3.customerApp.historicRoyalties.paidRoyaltiesTab',
  MSR3_PAYMENTS_ROUTE: 'msr3.customerApp.paymentsPage.route',
  MSR3_PROFILE_ROUTE: 'msr3.customerApp.profilePage.route',
  MSR3_RETURN_TO_MSR2_BANNER: 'msr3.customerApp.returnToMsr2Banner',
  /** Gates Royalties ANZ routes + Earnings Statements / Historic Royalties nav renames. Default off until tables ship. */
  MSR3_ROYALTIES_ANZ_ROUTE: 'msr3.customerApp.royaltiesAnzPage.route',
  /** Gates Royalties World routes + nav link. Default off until tables ship. */
  MSR3_ROYALTIES_WORLD_ROUTE: 'msr3.customerApp.royaltiesWorldPage.route',
  MSR3_SETTINGS_ROUTE: 'msr3.customerApp.settingsPage.route',
  MSR3_TASKS_ROUTE: 'msr3.customerApp.tasksPage.route',
  /** Gates the Home "View All" link on every task section, so they cannot drift apart per environment. */
  MSR3_TASKS_VIEW_ALL: 'msr3.customerApp.tasks.viewAll',
  MSR3_TITLE_PAGE_ROUTE: 'msr3.customerApp.titlePage.route',
  MSR3_TITLE_SUMMARY_ROUTE: 'msr3.customerApp.titleSummaryPage.route',
  /** Gates multi-bank account lists, add CTAs, and payment-rule copy in workspace bank settings. */
  MULTI_BANK_ACCOUNT: 'workspace.settings.bankDetails.multiBankAccount',
} as const;

/**
 * The flags behind the MSR3 home route (`/`). Anything that has to pick the
 * same shell as `/` (the root error boundary, /logged-out, the home nav links)
 * reads this, so adding a flag here moves them all together.
 */
export const MSR3_HOME_FLAGS: string[] = [
  FEATURE_FLAGS.MSR3_ENABLED,
  FEATURE_FLAGS.MSR3_HOME_ROUTE,
];
